import { Box, Stack, Typography } from "@mui/material";
import sanitizeHtml from "@/utils/sanitize-html";
import React from "react";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import Image from "next/image";

interface OurValuesTextCardProps {
  headerText?: string;
  copyText?: string;
  subtitle?: string;
  index?: number;
}

const extractListItemsHtml = (html?: string): string[] => {
  if (!html) return [];

  const sanitized = sanitizeHtml(html);
  const liMatches = sanitized.match(/<li\b[^>]*>([\s\S]*?)<\/li>/gi) || [];

  return liMatches
    .map((item) =>
      item
        .replace(/^<li\b[^>]*>/i, "")
        .replace(/<\/li>$/i, "")
        .trim()
    )
    .filter((item) => item.length > 0);
};

interface ParsedLink {
  href: string;
  label: string;
}

// extract link from richText
const extractLink = (html?: string): ParsedLink | null => {
  if (!html) return null;

  const sanitized = sanitizeHtml(html);
  // Strip <ul>...</ul> blocks so we only pick up anchors that live outside list items
  const withoutList = sanitized.replace(/<ul\b[^>]*>[\s\S]*?<\/ul>/gi, "");
  const ctaLink = withoutList.match(/<a\b[^>]*>[\s\S]*?<\/a>/i)?.[0];

  if (!ctaLink) return null;

  const href = ctaLink.match(/href=(["'])(.*?)\1/i)?.[2] ?? "#";
  const label = ctaLink.replace(/<[^>]+>/g, "").trim();

  if (!label) return null;

  return { href, label };
};

const OurValuesTextCard = (props: OurValuesTextCardProps) => {
  const { headerText, copyText, subtitle, index } = props;
  const listItems = React.useMemo(() => extractListItemsHtml(subtitle), [subtitle]);
  const link = React.useMemo(() => extractLink(subtitle), [subtitle]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "1.6rem", width: "100%" }}
      data-testid="our-values-text-card"
    >
      <Stack direction="row" alignItems="flex-start" gap="1.6rem">
        <Box>
          <Image src="/images/dot_icon.svg" alt="Radio icon" width={24} height={24} />
        </Box>
        <Stack spacing="0.8rem" sx={{ mt: { xs: -0.4, md: -0.7 } }}>
          <Typography
            sx={{ fontSize: { xs: "2rem", sm: "2.2rem", lg: "2.4rem" }, fontWeight: 600 }}
            data-testid={`core-value-header-${index}`}
          >
            {headerText}
          </Typography>
          {copyText && (
            <Typography
              sx={{ fontSize: 18, fontWeight: 500 }}
              data-testid={`core-value-copy-${index}`}
            >
              {copyText}
            </Typography>
          )}
        </Stack>
      </Stack>
      {listItems.length > 0 && (
        <Box
          component="ul"
          sx={{ listStyle: "none", m: 0, pl: "1.6rem", display: "grid", gap: "1.6rem" }}
        >
          {listItems.map((item, idx) => (
            <Box
              component="li"
              key={item}
              sx={{ display: "flex", alignItems: "flex-start", gap: "1.6rem" }}
            >
              <Image
                src="/images/arrow_bullet.svg"
                alt="arrow icon"
                width={18}
                height={18}
                data-testid={`arrow-bullet-${idx}`}
              />

              <Typography
                component="div"
                sx={{ "& p": { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: item }}
                data-testid={`list-item-${idx}`}
              />
            </Box>
          ))}
        </Box>
      )}
      {subtitle && listItems.length === 0 && (
        <Typography
          component="div"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(subtitle) }}
          data-testid={`core-value-subtitle-${index}`}
        />
      )}
      {link && (
        <Box
          sx={{
            mt: "0.8rem",
            ml: { xs: 0, lg: "1.6rem" },
            display: "flex",
            justifyContent: { lg: "flex-start", xs: "center" },
            width: "100%"
          }}
        >
          <ColletteButton
            testId="explore-link"
            component="a"
            size="large"
            href={link.href}
            label={link.label}
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};

export default OurValuesTextCard;
